import { StreamApi } from '../../api/StreamApi';
import { Log, Registry } from '@lightningjs/sdk';
import { Environment } from '../../config/environment';
import { GlobalSettingsKeys } from '../../api/models/GlobalSettingsKeys';
import { StreamOptions, StreamStatus } from './PlayerModel';
import { addProperty } from '../../api/analytics/AnalyticsUtil';
import { PropertyKeys } from '../../api/analytics/ClientEvents';
import { Backend } from '../../api/Backend';
import {
  IProperty,
  IStreamAssetResponseV2,
  IStreamKeepAliveRequestV2,
  IStreamManifestResponseV2,
  IStreamStartRequestV2,
  IStreamStartResponseV2,
} from '../../api/models/AndromeTV.types';
import { StreamInfo } from './streamInfo/StreamInfo';
import { StreamType } from '../../api/models/Shared.types';

export class StreamManager {
  private _backend: Backend;
  private _defaultKeepaliveTime: number;
  private _streamStatus: string;
  private _keepaliveErrorCallback: (err: any) => any;
  private _currentStreamInfo: StreamInfo | undefined;
  private _currentStream: IStreamStartResponseV2 | undefined;
  private manifest: IStreamManifestResponseV2 | undefined;
  private _maxKeepaliveErrors = 0;
  private _keepaliveInterval: number | undefined;
  private _reservedStream: IStreamStartResponseV2 | undefined;

  constructor(backend: Backend, keepaliveCallback: OmitThisParameter<(err: any) => void>) {
    this._backend = backend;
    this._defaultKeepaliveTime = 300000;
    this._streamStatus = StreamStatus.IDLE;
    this._keepaliveErrorCallback = keepaliveCallback;
  }

  /**
   * @param {StreamInfo} streamInfo
   * @returns {Promise<IStreamAssetResponseV2>}
   */
  start(streamInfo: StreamInfo): Promise<IStreamAssetResponseV2 | undefined> {
    this._currentStreamInfo = streamInfo;
    const startRequest = this._createStartRequest();
    if (startRequest) {
      return StreamApi.start(this._backend, startRequest).then(result => {
        Log.info('start stream', this._currentStreamInfo);
        this._currentStream = result;
        this._startKeepaliveInterval();
        return result.asset;
      });
    }
    return Promise.resolve(undefined);
  }

  getManifest() {
    if (this._currentStream && this._currentStream.asset) {
      return StreamApi.manifest(this._backend, this._currentStream.sid, this._currentStream.asset.mid).then(result => {
        this.manifest = result;
        return result;
      });
    }
    return Promise.resolve(undefined);
  }

  /**
   * @returns {IStreamStartRequestV2}
   * @private
   */
  _createStartRequest(): IStreamStartRequestV2 | undefined {
    Log.info('createStartStreamRequest', this._currentStreamInfo);
    if (
      this._backend.deviceProfile &&
      this._currentStreamInfo &&
      this._currentStreamInfo.streamType &&
      this._currentStreamInfo.resourceId
    ) {
      return {
        profileId: this._backend.deviceProfile.id,
        group: 'MAIN',
        device: this._backend.sessionInfo?.device.id,
        deviceType: Environment.system,
        packageType: 'DASH',
        drmType: this._backend.playerController.preferredDrmProvider,
        asset: {
          type: this._currentStreamInfo.streamType,
          reserve: false,
          resource: this._currentStreamInfo.resourceId,
          quoteId: this._currentStreamInfo.quoteId,
          maxResolution: 'HD',
          options: this._currentStreamInfo.resumeFromLive ? [StreamOptions.RESUME_FROM_LIVE] : undefined,
        },
        catalogId: this._backend.deviceProfile.profile.catalog,
      };
    }
    return undefined;
  }

  /**
   * @param {Object} params
   * @returns {Promise<*>}
   */
  end() {
    if (this._currentStream) {
      this._stopKeepaliveInterval();
      return StreamApi.stop(this._backend, this._currentStream.sid);
    } else {
      return Promise.resolve();
    }
  }

  setStreamStatus(streamStatus: string) {
    this._streamStatus = streamStatus;
  }

  /**
   * @private
   */
  _startKeepaliveInterval() {
    this._stopKeepaliveInterval();
    this._defaultKeepaliveTime =
      this._backend.globalSettings.numberValue(GlobalSettingsKeys.defaultKeepaliveTimeout) || 300000;
    this._maxKeepaliveErrors = this._backend.globalSettings.numberValue(GlobalSettingsKeys.maxKeepAliveErrors) || 3;
    const intervalTime = (
      (this._currentStream?.keepalive || this._defaultKeepaliveTime) *
      (Math.random() * 0.15 + 0.75)
    ).toFixed(0);
    Log.info('starting stream keepalive interval', `every ${intervalTime}ms`);
    this._keepaliveInterval = Registry.setInterval(() => {
      this._sendKeepalive();
    }, intervalTime);
  }

  /**
   * @private
   */
  _sendKeepalive() {
    const keepaliveBody: IStreamKeepAliveRequestV2 = {
      status: this._streamStatus,
      mid: this._currentStream?.asset?.mid,
    };
    if (this._currentStream) {
      Log.info('sending stream keepalive', keepaliveBody);
      StreamApi.keepalive(this._backend, this._currentStream.sid, keepaliveBody).catch(err => {
        this._maxKeepaliveErrors--;
        if (this._maxKeepaliveErrors < 0 || (err.code && err.code.toUpperCase() === 'SS_STREAM_GONE')) {
          Log.error('stopping stream because of error criteria', this._currentStream);
          this._keepaliveErrorCallback(err);
        } else {
          Log.warn('keepalive error', err);
        }
      });
    } else {
      Log.warn('keepalive error this._currentStream not defined');
    }
  }

  /**
   * @private
   */
  _stopKeepaliveInterval() {
    if (this._keepaliveInterval) {
      Log.info('stopping stream keepalive interval');
      Registry.clearInterval(this._keepaliveInterval);
      this._keepaliveInterval = undefined;
    }
  }

  deleteReservedStream() {
    if (!this._reservedStream) return;
    this._stopKeepaliveInterval();
    StreamApi.stop(this._backend, this._reservedStream.sid);
  }

  reserveStream(type: StreamType, assetId: string, quoteId: string): Promise<IStreamStartResponseV2 | undefined> {
    const reserveRequest = this._createReserveRequest(type, assetId, quoteId);
    if (reserveRequest) {
      return StreamApi.start(this._backend, reserveRequest).then(result => {
        this._reservedStream = result;
        return result;
      });
    }
    return Promise.resolve(undefined);
  }

  _createReserveRequest(type: StreamType, assetId: string, quoteId: string): IStreamStartRequestV2 | undefined {
    Log.info('createReserveRequest', type, assetId, quoteId);
    if (this._backend.deviceProfile && this._backend.sessionInfo) {
      return {
        profileId: this._backend.deviceProfile.id,
        group: 'MAIN',
        device: this._backend.sessionInfo.device.id,
        deviceType: Environment.system,
        packageType: 'DASH',
        drmType: 'WIDEVINE',
        asset: {
          type: type,
          reserve: true,
          resource: assetId,
          quoteId: quoteId,
          maxResolution: 'HD',
          options: undefined,
        },
        catalogId: this._backend.deviceProfile.profile.catalog,
      };
    }
    return undefined;
  }

  get streamType() {
    return this._currentStreamInfo?.streamType;
  }

  analyticsProperties(excluded: string[]) {
    const properties: IProperty[] = [];
    if (this._currentStream) {
      addProperty(PropertyKeys.streamId, this._currentStream.sid, excluded, properties);
    }
    if (this.manifest) {
      addProperty(PropertyKeys.manifest, this.manifest.url, excluded, properties);
    }
    return properties;
  }
}
